import { faPen, faTrashXmark } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  FlexBox,
  JoonUIColor,
  Modal,
  ModalHeader,
  SPACING,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

import { useViewQuestModalStore } from "./useViewQuestModalStore"
import { QUERY_KEYS } from "../../../constants"
import { deleteQuest } from "../../../networking/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { localStorageItems } from "../../../util/storage"
import {
  CoachUpsellModalType,
  useCoachUpsellModalStore,
} from "../../care/CoachUpsellModal"
import useCareTeam from "../../care/useCareTeam"
import { useAddEditQuestStore } from "../addEditQuestModal/useAddEditQuestStore"
import { useQuestGroupInstances } from "../useQuestGroups"

const EditQuestButton = () => {
  const { onOpen: openAddEditQuestModal } = useAddEditQuestStore()
  const { questInstance, onClose: closeViewQuestModal } =
    useViewQuestModalStore()
  const { t } = useTranslation()

  const onClickEditQuest = () => {
    if (!questInstance) return
    openAddEditQuestModal(questInstance.series)
    closeViewQuestModal()
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_EDIT_QUEST)
  }

  return (
    <button
      className="flex flex-col gap-2 items-center p-4 flex-1"
      onClick={onClickEditQuest}
    >
      <FontAwesomeIcon
        icon={faPen}
        fontSize={24}
        color={JoonUIColor.text.disabled}
      />
      <Typography variant="bodySmallBold" color={JoonUIColor.text.disabled}>
        {t("QUEST_HEADER_EDIT")}
      </Typography>
    </button>
  )
}

const DeleteQuestButton = () => {
  const { questInstance, onClose: closeViewQuestModal } =
    useViewQuestModalStore()
  const { onOpen: openCoachUpsellModal } = useCoachUpsellModalStore()
  const [isDeleting, setIsDeleting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const { questGroupInstances } = useQuestGroupInstances(questInstance)
  const queryClient = useQueryClient()
  const { hasCoach } = useCareTeam()
  const { t } = useTranslation()

  const isTherapistCreated =
    questInstance?.series?.assigner_profile?.role === UserRole.THERAPIST
  if (!questInstance || isTherapistCreated) return <></>

  const onClickDeleteQuest = async () => {
    try {
      setIsDeleting(true)
      if (!questGroupInstances.length) return
      const deleteQuestPromises = questGroupInstances.map((quest) =>
        deleteQuest(quest.series.id)
      )
      await Promise.all(deleteQuestPromises)
      trackAnalyticEvent(ANALYTIC_EVENTS.DELETE_QUEST)
      queryClient.invalidateQueries([QUERY_KEYS.QUEST_BOARD])
      queryClient.invalidateQueries([QUERY_KEYS.ALL_QUESTS])
      queryClient.invalidateQueries([QUERY_KEYS.PARENT_TASKS])
      toast.success(
        questGroupInstances.length > 1
          ? t("VQ_TOAST_DELETED_GROUP")
          : t("VQ_TOAST_DELETED_ONE")
      )
      setShowConfirmModal(false)
      closeViewQuestModal()
      if (hasCoach) return
      const lastSeenUpsellModalDate = localStorage.getItem(
        localStorageItems.deleteCoachUpsellModalSeenDate
      )
      const needsToSeeDeleteCoachUpsellModal =
        !dayjs().isSame(dayjs(lastSeenUpsellModalDate), "week") ||
        !lastSeenUpsellModalDate
      if (needsToSeeDeleteCoachUpsellModal)
        openCoachUpsellModal(CoachUpsellModalType.delete)
    } catch (error) {
      toast.error(t("VQ_TOAST_DELETE_ERROR"))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <button
        className="flex flex-col gap-2 items-center p-3 flex-1"
        onClick={() => setShowConfirmModal(true)}
      >
        <FontAwesomeIcon
          icon={faTrashXmark}
          fontSize={24}
          color={JoonUIColor.text.disabled}
        />
        <Typography variant="bodySmallBold" color={JoonUIColor.text.disabled}>
          {t("QB_DELETE")}
        </Typography>
      </button>
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        animate
      >
        <ModalHeader
          title={t("VQ_ARE_YOU_SURE")}
          onClose={() => setShowConfirmModal(false)}
          style={{ borderBottom: `1px solid ${JoonUIColor.border.default}` }}
        />
        <FlexBox
          style={{
            padding: `${SPACING.space4} ${SPACING.space6} ${SPACING.space6}`,
            width: "min(500px, 100vw)",
          }}
          gap={SPACING.space4}
        >
          <Typography variant="body">{t("VQ_DELETE_SERIES_DESC")}</Typography>

          <FlexBox
            align="center"
            justify="flex-end"
            wrap={false}
            gap={SPACING.space2}
          >
            <button onClick={() => setShowConfirmModal(false)}>
              <Typography
                variant="bodyBold"
                color={JoonUIColor.text.primaryAccent}
              >
                {t("QB_CANCEL")}
              </Typography>
            </button>
            <Button
              buttonType="redPrimary"
              onClick={onClickDeleteQuest}
              isLoading={isDeleting}
              text={t("QB_DELETE")}
            />
          </FlexBox>
        </FlexBox>
      </Modal>
    </>
  )
}

export { EditQuestButton, DeleteQuestButton }
